import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import TreeAPI from '../apis/TreeAPI';
import produce from 'immer';
import ListTitles from './v2/ListTitles';
import { ReactComponent as ArrowBack } from "../assets/svg/arrow-back.svg";
import Courses from './Courses';
import UserAPI from '../apis/UserAPI';
import { UserContext } from '../contexts/UserContext';
import { useCourses } from '../hooks/v2/useCourses';
import { useEpisodes } from '../hooks/v2/useEpisodes';
import { useChapters } from '../hooks/v2/useChapters';
import { useLessons } from '../hooks/v2/useLessons';
import { useLesson } from '../hooks/v2/useLesson';
import { useFree } from '../hooks/v2/useFree';
import { useCompleted } from '../hooks/v2/useCompleted';
import { usePurchases } from '../hooks/v2/usePurchases';
import Lesson from "./LessonMiddleware";
import { ReactComponent as MenuSVG } from "../assets/svg/menu.svg";
import Menu from "./Menu";
import VideoHelp from "./VideoHelp";
import Notifications from "./notifications/Notifications";
import { SetCompleted } from '../apis/User/indexv2';
import { Box } from '@mui/material';

const Home = () => {
  let { courseId: paramCourseId } = useParams<IHomeParams>();
  const userContext = useContext(UserContext);
  const userId: string | undefined = userContext?.id;
  const { courses, coursesArray } = useCourses();

  const smartCourseId =
    (
      paramCourseId !== undefined &&
      courses[paramCourseId] !== undefined
    )
      ? paramCourseId
      : undefined;

  const [isLoading, setIsLoading] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | undefined>(smartCourseId);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | undefined>(undefined);
  const [selectedChapterId, setSelectedChapterId] = useState<string | undefined>(undefined);
  const [selectedLessonId, setSelectedLessonId] = useState<string | undefined>(undefined);

  const { episodes, episodesArray } = useEpisodes({ courseId: selectedCourseId });
  const { chapters, chaptersArray } = useChapters({ episodeId: selectedEpisodeId });
  const { lessons, lessonsArray } = useLessons({ chapterId: selectedChapterId })

  const { completed, completedArray } = useCompleted({ userId: userId, episodeId: selectedEpisodeId, chapterId: selectedChapterId }); // sync
  const { purchases, purchasesArray } = usePurchases({ userId: userId }); // only episodes
  const { free, freeArray } = useFree(); // sync

  const [showVideoHelp, setShowVideoHelp] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    setSelectedCourseId(smartCourseId);
  }, [smartCourseId]);

  const GetNextEpisodeUncompletedAvailable = useCallback((array: typeof episodesArray) => {

    let firstUncompletedFreeEpisode = undefined;
    for (let index = 0; index < array.length; index++) {
      const episode = array[index];
      if (completed[episode.id] === undefined) 
        if (purchases[episode.id] === undefined) {
          if (free[episode.id] === true)
            if (firstUncompletedFreeEpisode !== undefined) firstUncompletedFreeEpisode = episode;
        } else return episode;
    }

    if (firstUncompletedFreeEpisode !== undefined) return firstUncompletedFreeEpisode;
    return null;
  }, [purchases, free, completed]);

  const GetNextChapterUncompletedAvailable = useCallback((array: typeof chaptersArray) => {
    if(selectedEpisodeId === undefined) throw "No está definido el id del episode";

    for (let index = 0; index < array.length; index++) {
      const chapter = array[index];
      if (completed[chapter.id] === undefined)
        if (purchases[selectedEpisodeId] === undefined) {
          if (free[chapter.id] === true) return chapter;
        } else return chapter;
    }

    return null;
  }, [selectedEpisodeId, purchases, free, completed]);

  const GetNextLessonUncompletedAvailable = useCallback((array: typeof lessonsArray) => {
    if (selectedEpisodeId === undefined) throw "No está definido el id del episode";

    for (let index = 0; index < array.length; index++) {
      const lesson = array[index];
      if (completed[lesson.id] === undefined)
        if (purchases[selectedEpisodeId] === undefined) {
          if (free[lesson.id] === true) return lesson;
        } else return lesson;
    }

    return null;
  }, [selectedEpisodeId, purchases, free, completed]);

  const GetItemProperties = useCallback((props: {
    isFree: boolean;
    isPurchased: boolean;
    isCompleted: boolean;
    itemId: string;
    nextItemId: string | null;
  }) => {
    const isNextItem = props.nextItemId !== null && props.nextItemId === props.itemId;
    return {
      isFree: props.isFree,
      isPurchased: props.isPurchased,
      isCompleted: props.isCompleted,
      isEnabled: (props.isPurchased && props.isCompleted) || (props.isPurchased && isNextItem) || (props.isPurchased === false && props.isFree)
    };

  }, []);

  const treeCourses = useMemo(() => {
    return (
      <Wrapper>
        <TitleWrapper>
          <Title>Cursos</Title>
        </TitleWrapper>
        <Courses
          data={coursesArray}
          getIsAllowed={(payload: IDBRealtimeCourse) => {
            const courseId = payload.id;
            let course = courses[courseId];
            if (course !== undefined && course.episodes !== undefined) {
              const episodesIds = Object.keys(course.episodes);

              for (let index = 0; index < episodesIds.length; index++) {
                const episodeId = episodesIds[index];
                if (course.episodes[episodeId] === true) {
                  if (purchases[episodeId] === true || free[episodeId] === true) {
                    return true;
                  }
                }
              }
            }
            return false;
          }}
          onClick={(course: IDBRealtimeCourse) => setSelectedCourseId(course.id)}
          className={{}}
        />
      </Wrapper>
    );
  }, [courses, coursesArray, purchases, free]);

  const treeEpisodes = useMemo(() => {
    if (selectedCourseId === undefined) return;
    const course = courses[selectedCourseId];
    if (course === undefined) return;

    const nextEpisode = GetNextEpisodeUncompletedAvailable(episodesArray);

    return (
      <Wrapper>
        <TitleWrapper onClick={() => setSelectedCourseId(undefined)}>
          <Back as={ArrowBack} />
          <Title>{course.title}</Title>
        </TitleWrapper>
        <ListTitles
          items={episodesArray.map(episode => {
            const properties = GetItemProperties({
              itemId: episode.id,
              nextItemId: nextEpisode === null ? null : nextEpisode.id,
              isFree: free[episode.id] === true,
              isPurchased: purchases[episode.id] === true,
              isCompleted: completed[episode.id] === true
            });
            return {
              id: episode.id,
              label: episode.title,
              ...properties
            };
          })}
          onClick={(episode) => setSelectedEpisodeId(episode.id)}
        />
      </Wrapper>
    );
  }, [
    selectedCourseId,
    courses,
    episodesArray,
    purchases,
    free,
    completed,
    GetNextEpisodeUncompletedAvailable,
    GetItemProperties
  ]);

  const treeChapters = useMemo(() => {
    if (selectedEpisodeId === undefined) return;
    const episode = episodes[selectedEpisodeId];
    if (episode === undefined) return;

    const nextChapter = GetNextChapterUncompletedAvailable(chaptersArray);

    return (
      <Wrapper>
        <TitleWrapper onClick={() => setSelectedEpisodeId(undefined)}>
          <Back as={ArrowBack} />
          <Title>{episode.title}</Title>
        </TitleWrapper>
        <ListTitles
          items={chaptersArray.map(chapter => {
            const properties = GetItemProperties({
              itemId: chapter.id,
              nextItemId: nextChapter === null ? null : nextChapter.id,
              isFree: free[chapter.id] === true,
              isPurchased: purchases[selectedEpisodeId] === true,
              isCompleted: completed[chapter.id] === true
            });
            return {
              id: chapter.id,
              label: chapter.title,
              ...properties
            };
          })}
          onClick={(chapter) => setSelectedChapterId(chapter.id)}
        />
      </Wrapper>
    );
  }, [
    selectedEpisodeId,
    episodes,
    chaptersArray,
    purchases,
    free,
    completed,
    GetNextChapterUncompletedAvailable,
    GetItemProperties
  ]);

  const treeLessons = useMemo(() => {
    if (selectedChapterId === undefined || selectedEpisodeId === undefined) return;
    const chapter = chapters[selectedChapterId];
    if (chapter === undefined) return;

    const nextLesson = GetNextLessonUncompletedAvailable(lessonsArray);

    return (
      <Wrapper>
        <TitleWrapper onClick={() => setSelectedChapterId(undefined)}>
          <Back as={ArrowBack} />
          <Title>{chapter.title}</Title>
        </TitleWrapper>
        <ListTitles
          items={lessonsArray.map(lesson => {
            const properties = GetItemProperties({
              itemId: lesson.id,
              nextItemId: nextLesson === null ? null : nextLesson.id,
              isFree: free[lesson.id] === true,
              isPurchased: purchases[selectedEpisodeId] === true,
              isCompleted: completed[lesson.id] === true
            });
            return {
              id: lesson.id,
              label: lesson.title,
              ...properties
            };
          })}
          onClick={(lesson) => setSelectedLessonId(lesson.id)}
        />
      </Wrapper>
    );
  }, [
    selectedChapterId,
    selectedEpisodeId,
    chapters,
    lessonsArray,
    purchases,
    free,
    completed,
    GetNextLessonUncompletedAvailable,
    GetItemProperties
  ]);

  const handleOnCompleted = useCallback(async (props: { episodeId: string; chapterId: string; lessonId: string; }) => {
    if (completed[props.lessonId] !== undefined) return;
    if (userId === undefined) return;

    try {
      setIsCompleting(true);
      await SetCompleted({
        userId: userId,
        episodeId: props.episodeId,
        chapterId: props.chapterId,
        lessonId: props.lessonId,
      });
    } catch (error) {

    } finally {
      setIsCompleting(false);
    }
  }, [
    completed,
    userId,
  ]);

  const lessonDisplay = useMemo(() => {
    if (
      selectedLessonId === undefined ||
      selectedEpisodeId === undefined ||
      selectedChapterId === undefined ||
      selectedCourseId === undefined
    ) return;

    const lesson = lessons[selectedLessonId];
    if (lesson === undefined) return;

    const lessonNext = GetNextLessonUncompletedAvailable(lessonsArray);
    
    return (
      <Wrapper>
        <TitleWrapper onClick={() => setSelectedLessonId(undefined)}>
          <Back as={ArrowBack} />
          <Title>{lesson.title}</Title>
        </TitleWrapper>
        <Lesson
          id={lesson.id}
          courseId={selectedCourseId}
          episodeId={selectedEpisodeId}
          chapterId={selectedChapterId}
          onCompleted={() => {
            handleOnCompleted({
              episodeId: selectedEpisodeId,
              chapterId: selectedChapterId,
              lessonId: selectedLessonId
            });
          }}
          hasNextLesson={lessonNext !== null}
          onGoToNextLesson={() => {
            if (lessonNext !== null) {
              setIsLoading(true);
              setSelectedLessonId(undefined);
              let id = lessonNext.id;
              setTimeout(() => { // You have to put a timeout to force a re-render
                setIsLoading(false);
                setSelectedLessonId(id);
              }, 100);
            }
          }}
          onGoToChaptersView={() => {
            setSelectedChapterId(undefined);
            setSelectedLessonId(undefined);
          }}
          isCompleted={completed[lesson.id] === true}
        />
      </Wrapper>
    );
  }, [
    selectedLessonId,
    selectedChapterId,
    selectedEpisodeId,
    selectedCourseId,
    lessonsArray,
    lessons,
    completed,
    handleOnCompleted,
    GetNextLessonUncompletedAvailable
  ]);

  const stage = useMemo(() => {
    if (selectedLessonId !== undefined) return 'SHOW_LESSON';
    else if (selectedChapterId !== undefined) return 'SHOW_LESSONS';
    else if (selectedEpisodeId !== undefined) return 'SHOW_CHAPTERS';
    else if (selectedCourseId !== undefined) return 'SHOW_EPISODES';
    else return 'SHOW_COURSES';
  }, [selectedChapterId, selectedCourseId, selectedEpisodeId, selectedLessonId]);

  return (
    <>
      <GlobalStyle />
      <ContainerWrapper>
        <Notifications
          onGoToLesson={({ courseId, episodeId, chapterId, lessonId }: any) => {
            setSelectedCourseId(courseId);
            setSelectedEpisodeId(episodeId);
            setSelectedChapterId(chapterId);
            setSelectedLessonId(lessonId);
          }}
        />
        {showMenu && <Menu onClose={() => setShowMenu(false)} />}
        {showVideoHelp && <VideoHelp onClose={() => setShowVideoHelp(false)} />}
        <Container>
          <Bar>
            <MenuIcon as={MenuSVG} onClick={() => setShowMenu(true)} />
          </Bar>
          {isLoading === true && <Spinner src="loading-blue.gif" />}
          {isLoading === false && (
            <>
              {stage === "SHOW_COURSES" && treeCourses}
              {stage === "SHOW_EPISODES" && treeEpisodes}
              {stage === "SHOW_CHAPTERS" && treeChapters}
              {stage === "SHOW_LESSONS" && treeLessons}
              {stage === "SHOW_LESSON" && lessonDisplay}
            </>
          )}
          {stage !== 'SHOW_LESSON' && (
            <Footer>
              <Help onClick={() => setShowVideoHelp(true)}>
                ¿Necesitas ayuda? <strong>Video aquí</strong>
              </Help>
            </Footer>
          )}
          {isCompleting === true && stage === 'SHOW_LESSON' && (
            <Box sx={{
              pt: 4
            }}>
              <Spinner src="loading-blue.gif" />
            </Box>
          )}
        </Container>
      </ContainerWrapper>
    </>);
}

export default Home;


const GlobalStyle = createGlobalStyle`
  body {
    background-color: #32b0ea;
  }
`


const ContainerWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background-color: #32b0ea;
  padding: 10px;
  box-sizing: border-box;
`;

const Container = styled.div`
  max-width: 450px;
  width: 100%;
`;

const Wrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  padding: 5px 0px;
  margin-bottom: 25px;
  cursor: pointer;

  user-select: none;
`;

const Back = styled.div`
  fill: white;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  margin-right: 15px;
`;

const Title = styled.div`
  font-size: 1.5em;
  font-weight: 600;
  color: white;
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 15px;
`;

const MenuIcon = styled.div`
  fill: white;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  padding: 5px;
  cursor: pointer;
`;

const Spinner = styled.img`
  margin: auto;
  width: 25px;
  height: 25px;
  display: block;
`;

const Footer = styled.div`
  display: flex;
`;

const Help = styled.div`
  font-size: 0.9em;
  color: white;
  margin-top: 10px;
  cursor: pointer;
  padding: 10px;
`;